import React from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import Qrcode from '@/lib/Qrcode/Qrcode';

interface OtpQrCodeProps {
  qrUrl: string;
  secret: string;
  title?: string;
  description?: string;
}

/**
 * OTP 로그인을 위한 QR 코드 컴포넌트
 *
 * SOLID 원칙 적용:
 * - Single Responsibility: QR 코드 표시와 OTP 정보 표시만 담당
 * - Open/Closed: props를 통해 확장 가능하도록 설계
 * - Liskov Substitution: 기존 Qrcode 컴포넌트를 재사용
 * - Interface Segregation: 필요한 props만 받도록 인터페이스 분리
 * - Dependency Inversion: 구체적인 구현보다는 추상화에 의존
 */
export default function OtpQrCode({
  qrUrl,
  secret,
  title = 'OTP 인증 QR 코드',
  description = ' OTP 앱으로 QR 코드를 스캔하세요.',
}: OtpQrCodeProps) {
  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        maxWidth: 400,
        mx: 'auto',
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {description}
      </Typography>

      {/* QR 코드 표시 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Qrcode value={qrUrl} />
      </Box>

      {/* Secret 키 표시 (수동 입력용) */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>수동 입력용 Secret Key:</strong>
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: 'grey.100',
            p: 1,
            borderRadius: 1,
            wordBreak: 'break-all',
          }}
        >
          {secret}
        </Typography>
      </Alert>

      <Typography variant="caption" color="text.secondary">
        QR 코드 스캔이 어려운 경우 위의 Secret Key를 직접 입력하세요.
      </Typography>
    </Paper>
  );
}
